import {
  AdminTenantDetails,
  MonitoringUsersGroup,
} from '@hakimo-ui/hakimo/types';
import { useUpdateHakimoMonitoring } from '@hakimo-ui/hakimo/data-access';
import { toast } from '@hakimo-ui/hakimo/util';
import { useEffect, useMemo, useState } from 'react';
import {
  ENABLE_MONITORING_CHECKLIST,
  getMonitoringOptions,
  getMonitoringUpdatePayload,
} from '../util';
import { MonitoringTenantsFooter } from './MonitoringTenantsFooter';
import { MonitoringTenantsForm } from './MonitoringTenantsForm';

interface Props {
  tenant: AdminTenantDetails;
  open: boolean;
  groups: MonitoringUsersGroup[] | undefined;
  onClose: () => void;
  onBusyChange: (busy: boolean) => void;
}

export function MonitoringTenantsTab({
  tenant,
  open,
  groups,
  onClose,
  onBusyChange,
}: Props) {
  const {
    allMonitoringTenants,
    allHighPriorityOperatorEmails,
    enabledOperatorEmails,
  } = useMemo(
    () => getMonitoringOptions(tenant.hakimoMonitoring),
    [tenant.hakimoMonitoring]
  );

  const [selectedMonitoringTenants, setSelectedMonitoringTenants] = useState<
    Record<string, boolean>
  >({});
  const [selectedOperators, setSelectedOperators] = useState<
    Record<string, boolean>
  >({});
  const [checklist, setChecklist] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!open) return;
    const initialTenants: Record<string, boolean> = {};
    allMonitoringTenants.forEach((t) => {
      initialTenants[t.id] = true;
    });
    setSelectedMonitoringTenants(initialTenants);
    const initialOperators: Record<string, boolean> = {};
    allHighPriorityOperatorEmails.forEach((email) => {
      initialOperators[email] = enabledOperatorEmails.has(email);
    });
    setSelectedOperators(initialOperators);
    setChecklist({});
  }, [
    open,
    allMonitoringTenants,
    allHighPriorityOperatorEmails,
    enabledOperatorEmails,
  ]);

  const updateMonitoringMutation = useUpdateHakimoMonitoring(
    tenant.id,
    (data) => {
      const action = data.payload.enabled ? 'enabled' : 'disabled';
      toast(`Hakimo monitoring ${action} successfully`, { type: 'success' });
      onClose();
    },
    () => {
      toast('Failed to update Hakimo monitoring', { type: 'error' });
    }
  );

  const isUpdating = updateMonitoringMutation.isLoading;

  useEffect(() => {
    onBusyChange(isUpdating);
  }, [isUpdating, onBusyChange]);

  const allChecklistDone = ENABLE_MONITORING_CHECKLIST.every(
    (item) => checklist[item.id]
  );
  const hasAnySelection =
    allMonitoringTenants.some((t) => selectedMonitoringTenants[t.id]) ||
    allHighPriorityOperatorEmails.some((email) => selectedOperators[email]);
  const canApplyTenants = hasAnySelection ? allChecklistDone : true;

  const handleApplyTenants = () => {
    updateMonitoringMutation.mutate(
      getMonitoringUpdatePayload(
        tenant.id,
        allMonitoringTenants,
        allHighPriorityOperatorEmails,
        selectedMonitoringTenants,
        selectedOperators,
        enabledOperatorEmails
      )
    );
  };

  const handleDisableAll = () => {
    const cleared: Record<string, boolean> = {};
    allMonitoringTenants.forEach((t) => {
      cleared[t.id] = false;
    });
    setSelectedMonitoringTenants(cleared);
    setSelectedOperators({});
  };

  return (
    <div className="flex h-full flex-col">
      <MonitoringTenantsForm
        tenant={tenant}
        isUpdating={isUpdating}
        allMonitoringTenants={allMonitoringTenants}
        allHighPriorityOperatorEmails={allHighPriorityOperatorEmails}
        groups={groups}
        selectedMonitoringTenants={selectedMonitoringTenants}
        selectedOperators={selectedOperators}
        checklist={checklist}
        onChangeMonitoringTenant={(id, checked) =>
          setSelectedMonitoringTenants((prev) => ({ ...prev, [id]: checked }))
        }
        onChangeOperator={(email, checked) =>
          setSelectedOperators((prev) => ({ ...prev, [email]: checked }))
        }
        onChangeChecklist={(id, checked) =>
          setChecklist((prev) => ({ ...prev, [id]: checked }))
        }
      />
      <MonitoringTenantsFooter
        isUpdating={isUpdating}
        submitLoading={updateMonitoringMutation.isLoading}
        canApply={canApplyTenants}
        hasAnySelection={hasAnySelection}
        onSubmit={handleApplyTenants}
        onCancel={onClose}
        onDisableAll={handleDisableAll}
      />
    </div>
  );
}

export default MonitoringTenantsTab;
